import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Check, Copy, Loader2, Send, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/KpiCard";
import { SmartTable } from "@/components/SmartTable";
import { api, errMsg, money } from "@/lib/api";

export const CHAMPS_SAISIS = [["taxes", "Taxes payées par le voyageur"], ["total", "Total payé par le voyageur"], ["commission", "Frais de service d'hôte"],
  ["taxes_hote", "Taxes versées à l'hôte"], ["net", "Vous gagnez"]];

const LireCapture = ({ pf, onLu }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const lire = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length) { return; }
    if (files.length > 3) { toast.error("3 captures au maximum"); return; }
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("plateforme", pf);
    setBusy(true);
    try {
      const { data } = await api.post("/calcul-reservation/lire-capture", fd);
      onLu(data);
      toast.success("Montants lus sur la capture : vérifiez-les avant de comparer.");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={lire} data-testid="saisi-capture-input" />
      <Button size="sm" variant="outline" className="h-8 bg-white" disabled={busy} onClick={() => ref.current.click()} data-testid="saisi-lire-capture">
        {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Camera className="mr-1 h-3.5 w-3.5" />}Lire une capture
      </Button>
    </>
  );
};

export const SaisiePlateforme = ({ pf, valeurs, set, remplir, onLu, children }) => {
  const vide = !Object.values(valeurs).some((v) => String(v ?? "").trim());
  return (
    <Panel title={`Montants affichés par ${pf} (facultatif)`} testId="saisi-panel" action={
      <div className="flex flex-wrap gap-2">
        <LireCapture pf={pf} onLu={(d) => { remplir(Object.fromEntries(CHAMPS_SAISIS.map(([k]) => [k, d[k] == null ? "" : String(d[k]).replace(".", ",")]))); onLu(d); }} />
        {!vide && <Button size="sm" variant="ghost" className="h-8" onClick={() => remplir({})} data-testid="saisi-vider"><X className="mr-1 h-3.5 w-3.5" />Vider</Button>}
      </div>}>
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {CHAMPS_SAISIS.map(([k, l]) => (
          <label key={k} className="space-y-1.5 text-sm"><span className="block text-xs font-medium text-slate-600">{l} ($)</span>
            <Input inputMode="decimal" value={valeurs[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder="—" data-testid={`saisi-${k}`} />
          </label>
        ))}
      </div>
      {children}
      <p className="mt-3 text-xs text-slate-500">Recopiez les montants de l'écran « Revenus » de {pf} ou lisez une à trois captures d'écran : l'écart avec le calcul exact s'affiche dans le résultat.</p>
    </Panel>
  );
};

const fr = (v) => String(v).replace(".", ",");

const rabaisLu = (lu, type) => {
  const r = lu.rabais || 0;
  if (!r) { return { ajustement: "", ajustement_type: "pct", texte: "aucun" }; }
  if (type === "nuit") { const v = Math.round((r / lu.nuits) * 100) / 100; return { ajustement: fr(v), ajustement_type: "nuit", texte: `${money(v)} par nuit (${money(v * lu.nuits)} pour le séjour)` }; }
  if (type === "montant") { return { ajustement: fr(r), ajustement_type: "montant", texte: `${money(r)} pour le séjour` }; }
  const p = lu.rabais_pct ?? Math.round((r / (lu.prix_nuit * lu.nuits)) * 10000) / 100;
  return { ajustement: fr(p), ajustement_type: "pct", texte: `${fr(p)} % du prix` };
};

export const ApercuCapture = ({ lu, onAppliquer, onIgnorer }) => {
  const [type, setType] = useState("nuit");
  const ok = lu.prix_nuit > 0 && lu.nuits >= 1;
  const r = ok ? rabaisLu(lu, type) : null;
  return (
    <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50/60 p-4 text-sm" data-testid="capture-apercu">
      <p className="font-semibold text-violet-900">Réservation lue sur la capture</p>
      {!ok ? <p className="mt-1 text-slate-600" data-testid="capture-apercu-incomplet">Le prix par nuit ou le nombre de nuits n'a pas pu être lu : remplissez la réservation à la main.</p> : (<>
        <p className="mt-1 text-slate-700" data-testid="capture-apercu-valeurs">Prix par nuit {money(lu.prix_nuit)} · {lu.nuits} nuit(s) · ménage {money(lu.menage || 0)} · rabais {r.texte}</p>
        {lu.rabais > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2"><span className="text-xs text-slate-600">Saisir le rabais :</span>
            {[["nuit", "En $ par nuit"], ["montant", "En $ pour le séjour"], ["pct", "En % du prix"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setType(v)} data-testid={`capture-rabais-${v}`}
                className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${type === v ? "border-violet-400 bg-white font-semibold text-violet-900" : "border-transparent text-slate-600 hover:bg-white"}`}>{l}</button>
            ))}
          </div>
        )}
      </>)}
      <div className="mt-3 flex gap-2">
        {ok && <Button size="sm" className="h-8" onClick={() => onAppliquer({ montant: fr(lu.prix_nuit), nuits: String(lu.nuits), frais_menage: fr(lu.menage || 0), ajustement: r.ajustement, ajustement_type: r.ajustement_type })} data-testid="capture-appliquer"><Check className="mr-1 h-3.5 w-3.5" />Appliquer à la réservation</Button>}
        <Button size="sm" variant="ghost" className="h-8" onClick={onIgnorer} data-testid="capture-ignorer">Ignorer</Button>
      </div>
    </div>
  );
};

export const AnnulerRemplissage = ({ onAnnuler }) => (
  <div className="mt-4 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-2 text-sm text-emerald-900" data-testid="capture-applique">
    Réservation remplie depuis la capture.
    <Button size="sm" variant="outline" className="h-7 bg-white" onClick={onAnnuler} data-testid="capture-annuler"><Undo2 className="mr-1 h-3.5 w-3.5" />Annuler</Button>
  </div>
);

export const MessagePlateforme = ({ message, pf, corps, onSaved }) => {
  const [langue, setLangue] = useState("fr");
  const [envoye, setEnvoye] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!message) { return null; }
  const copier = () => navigator.clipboard.writeText(message[langue]).then(() => toast.success("Message copié")).catch(() => toast.error("Copie impossible : sélectionnez le texte"));
  const marquer = async () => {
    setBusy(true);
    try {
      const n = new Date();
      const jour = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
      const { data } = await api.post("/demandes", { calcul: corps, langue, date_envoi: jour });
      setEnvoye(data);
      onSaved();
      toast.success(data.reservation_id ? "Demande enregistrée dans les Demandes et le suivi de la réservation" : "Demande enregistrée dans les Demandes et l'historique des calculs");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5" data-testid="message-plateforme">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-800">Message à {pf}</h3>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {[["fr", "Français"], ["en", "English"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setLangue(v)} data-testid={`message-langue-${v}`}
              className={`rounded-md px-2.5 py-1 text-xs transition-colors ${langue === v ? "bg-white font-semibold text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
          ))}
        </div>
        <Button size="sm" variant="outline" className="ml-auto h-8 bg-white" onClick={copier} data-testid="message-copier"><Copy className="mr-1 h-3.5 w-3.5" />Copier</Button>
        {envoye ? <Link to="/demandes" className="inline-flex h-8 items-center gap-1 rounded-md bg-emerald-50 px-3 text-xs font-medium text-emerald-800 hover:underline" data-testid="message-envoye"><Check className="h-3.5 w-3.5" />Envoyé le {envoye.date_envoi} · voir les demandes</Link>
          : <Button size="sm" className="h-8" disabled={busy} onClick={marquer} data-testid="message-marquer-envoye">{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />}Marquer comme envoyé</Button>}
      </div>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-4 font-sans text-sm text-slate-700" data-testid="message-texte">{message[langue]}</pre>
    </div>
  );
};

const Td = ({ v, cls = "" }) => <td className={`px-3 py-2 text-right tabular ${cls}`}>{v == null ? "—" : money(v)}</td>;

export const EcartsSaisis = ({ c, pf }) => (
  <div className="mt-5" data-testid="saisi-ecarts">
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-emerald-800">Écarts avec les montants affichés par {pf}</h3>
    <div className="-mx-5 overflow-x-auto">
      <SmartTable id="calcul-ecarts-saisis" data-export-titre={`Écarts avec ${pf}`} className="w-full min-w-[820px] text-sm" data-testid="saisi-ecarts-table">
        <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          <th className="px-3 py-2 text-left">Élément</th><th className="px-3 py-2 text-right">{pf} (saisi)</th><th className="px-3 py-2 text-right">Exact (RQ)</th>
          <th className="px-3 py-2 text-right">Exact − saisi</th><th className="px-3 py-2 text-right">Calcul {pf}</th><th className="px-3 py-2 text-right">Calcul {pf} − saisi</th>
        </tr></thead>
        <tbody>
          {c.ecarts_saisis.map((x) => (
            <tr key={x.cle} className="border-t border-slate-100" data-testid={`saisi-ecart-${x.cle}`}>
              <td className="px-3 py-2"><span className="mr-2 font-mono text-xs text-slate-500">({x.lettre})</span>{x.libelle}</td>
              <Td v={x.saisi} cls="text-violet-800" /><Td v={x.exact} /><Td v={x.ecart_exact} cls={x.ecart_exact ? "font-semibold text-red-700" : "text-slate-400"} />
              <Td v={x.erreur} /><Td v={x.ecart_calcul} cls={x.ecart_calcul ? "text-amber-700" : "text-slate-400"} />
            </tr>
          ))}
        </tbody>
      </SmartTable>
    </div>
  </div>
);
